import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Box } from "@mui/system";
import {
  Button,
  Typography,
  Card,
  TextField,
  CircularProgress,
  Alert,
  InputLabel,
  FormHelperText,
  Divider,
} from "@mui/material";

import {
  ADD_BANKING_ACC,
  ALL_BANKING_ACC,
  ALL_PAYMENT_SERVICE_NAMES,
} from "../../gql/bankingAcc";
import RichTextEditor from "react-rte";

const toolbarConfig = {
  display: [
    "INLINE_STYLE_BUTTONS",
    "BLOCK_TYPE_BUTTONS",
    "LINK_BUTTONS",
    "BLOCK_TYPE_DROPDOWN",
    "HISTORY_BUTTONS",
  ],
  INLINE_STYLE_BUTTONS: [
    { label: "Bold", style: "BOLD" },
    { label: "Italic", style: "ITALIC" },
    { label: "Underline", style: "UNDERLINE" },
  ],
  BLOCK_TYPE_DROPDOWN: [
    { label: "Normal", style: "unstyled" },
    { label: "Heading Large", style: "header-one" },
    { label: "Heading Medium", style: "header-two" },
    { label: "Heading Small", style: "header-three" },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: "UL", style: "unordered-list-item" },
    { label: "OL", style: "ordered-list-item" },
  ],
};

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const CreateBankingAcc = ({ handleClose, packageId }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const [information, setInformation] = useState(
    RichTextEditor.createEmptyValue()
  );
  const [informationSub, setInformationSub] = useState(
    RichTextEditor.createEmptyValue()
  );
  const [informationPoint, setInformationPoint] = useState(
    RichTextEditor.createEmptyValue()
  );

  const { data: existingNamesData } = useQuery(ALL_PAYMENT_SERVICE_NAMES);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    delete errors[prop];
    setErrors({ ...errors });
  };

  const [createVideo] = useMutation(ADD_BANKING_ACC, {
    onError: (error) => {
      console.log("error : ", error);
      alert("Manual Payment Upload Error");
      setLoading(false);
    },
    onCompleted: (result) => {
      setValues({});
      setErrors({});
      setLoading(false);
      alert("Manual Payment has been Created");
      handleClose();
    },
    refetchQueries: [ALL_BANKING_ACC],
  });

  const onChangeInformation = (value) => {
    setInformation(value);
    setValues({ ...values, information: value.toString("html") });
  };
  const onChangeInformationSub = (value) => {
    setInformationSub(value);
    setValues({ ...values, information_sub: value.toString("html") });
  };

  const onChangeInformationPoint = (value) => {
    setInformationPoint(value);
    setValues({ ...values, information_point: value.toString("html") });
  };

  const handleCreate = async () => {
    setLoading(true);
    setErrors({});
    let isErrorExit = false;
    let errorObject = {};

    if (!values.payment_service_name) {
      errorObject.payment_service_name = "Payment Service Name is required.";
      isErrorExit = true;
    }
    if (!values.receiver_name) {
      errorObject.receiver_name = "Receiver Name is required.";
      isErrorExit = true;
    }
    if (!values.account_number) {
      errorObject.account_number = "Account Number is required.";
      isErrorExit = true;
    }

    if (existingNamesData) {
      const existingNames = existingNamesData?.banking_accounts?.map(
        (acc) => acc.payment_service_name
      );
      if (existingNames.includes(values.payment_service_name)) {
        errorObject.payment_service_name = "Payment Service Name must be unique.";
        isErrorExit = true;
      }
    }

    if (isErrorExit) {
      setErrors({ ...errorObject });
      setLoading(false);
      return;
    }

    try {
      await createVideo({
        variables: {
          addBankingAcc: {
            payment_service_name: values.payment_service_name,
            receiver_name: values.receiver_name,
            account_number: values.account_number,
            information: values?.information,
            information_sub: values?.information_sub,
            information_point: values?.information_point,
          },
        },
      });
    } catch (error) {
      console.log("error : ", error);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", display: "flex", flexDirection: "column", maxHeight: "90vh", borderRadius: "12px", overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Create Manual Payment (Banking Account)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Set up a manual payment options details for customers
          </Typography>
        </Box>
        <Button onClick={handleClose} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ overflowY: "auto", p: 4, flex: 1 }}>
        {/* Account Information Card */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Account Details</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="payment_service_name"
              label="Payment Service Name *"
              fullWidth
              value={values.payment_service_name || ""}
              onChange={handleChange("payment_service_name")}
              error={!!errors.payment_service_name}
              helperText={errors.payment_service_name}
              placeholder="e.g. KPay, CBPay, WavePay"
            />
            <TextField
              id="receiver_name"
              label="Receiver Name *"
              fullWidth
              value={values.receiver_name || ""}
              onChange={handleChange("receiver_name")}
              error={!!errors.receiver_name}
              helperText={errors.receiver_name}
              placeholder="e.g. U Kyaw Kyaw"
            />
            <TextField
              id="account_number"
              label="Account Number *"
              fullWidth
              value={values.account_number || ""}
              onChange={handleChange("account_number")}
              error={!!errors.account_number}
              helperText={errors.account_number}
              placeholder="e.g. 09123456789"
              sx={{ gridColumn: "1 / -1" }}
            />
          </Box>
        </Card>

        {/* Dynamic Payment Instruction Information Card */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Payment Information & Instructions</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
            {/* Shopping Info */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Information Shopping
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChangeInformation}
                value={information}
                toolbarConfig={toolbarConfig}
              />
              {errors.information && <FormHelperText error>{errors.information}</FormHelperText>}
            </Box>

            {/* Subscription Info */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Information Subscription
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChangeInformationSub}
                value={informationSub}
                toolbarConfig={toolbarConfig}
              />
              {errors.information_sub && <FormHelperText error>{errors.information_sub}</FormHelperText>}
            </Box>

            {/* Point Purchase Info */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Information Point
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChangeInformationPoint}
                value={informationPoint}
                toolbarConfig={toolbarConfig}
              />
              {errors.information_point && <FormHelperText error>{errors.information_point}</FormHelperText>}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #eee",
          px: 4,
          py: 2.5,
        }}
      >
        <Button onClick={handleClose} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={loading}
          onClick={handleCreate}
          sx={{
            minWidth: 140,
            bgcolor: "#ff5722",
            "&:hover": { bgcolor: "#e64a19" },
            "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
          {loading ? "Creating..." : "Create Account"}
        </Button>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em", zIndex: 9999 }} severity="success">{showAlert.message}</Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em", zIndex: 9999 }} severity="error">{showAlert.message}</Alert>
      )}
    </Box>
  );
};

export default CreateBankingAcc;
